import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Button,
  Alert,
  Snackbar,
  Fab,
  Tooltip,
  Paper,
  IconButton,
  ButtonGroup,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  TableChart as TableIcon,
  Dashboard as DashboardIcon
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import {
  fetchRenewalMetricsRequested,
  fetchRenewalFiltersRequested,
  fetchRenewalChartsRequested,
  fetchRenewalPoliciesRequested,
  refreshRenewalDashboardRequested,
  setCurrentFilters,
  clearCurrentFilters,
  openPoliciesPanel,
  closePoliciesPanel
} from '@/store/slices/renewalDashboardSlice';
import RenewalMetricsCards from '@/components/renewal/RenewalMetricsCards';
import RenewalChartsSection from '@/components/renewal/RenewalChartsSection';
import RenewalFiltersPanel from '@/components/renewal/RenewalFiltersPanel';
import RenewalPoliciesTable from '@/components/renewal/RenewalPoliciesTable';
import { RenewalDashboardFilters, RenewalDashboardFilterOptions } from '@/types/renewalDashboard';
import { PageHeader } from '@/components/ui/PageHeader';
import { PageLayout } from '@/components/ui/PageLayout';

const RenewalDashboard: React.FC = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    metrics,
    metricsLoading,
    metricsError,
    filters,
    filtersLoading,
    filtersError,
    smsSuccessChart,
    expiryDistributionChart,
    monthTrendChart,
    chartsLoading,
    chartsError,
    policies,
    policiesLoading,
    policiesError,
    policiesPagination,
    currentFilters,
    isPoliciesOpen
  } = useSelector((state: RootState) => state.renewalDashboard);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });

  // Initial data loading
  useEffect(() => {
    dispatch(fetchRenewalFiltersRequested());
    dispatch(fetchRenewalMetricsRequested({}));
    dispatch(fetchRenewalChartsRequested({ chartType: 'sms-success-rate', filters: {} }));
    dispatch(fetchRenewalChartsRequested({ chartType: 'expiry-distribution', filters: {} }));
    dispatch(fetchRenewalChartsRequested({ chartType: 'month-wise-trend', filters: {} }));
  }, [dispatch]);

  // Load policies when filters change
  useEffect(() => {
    if (Object.keys(currentFilters).length > 0) {
      dispatch(fetchRenewalPoliciesRequested({
        filters: currentFilters,
        pagination: {
          page: 1,
          page_size: 50,
          sort_by: 'created_at',
          sort_order: 'desc'
        }
      }));
    }
  }, [dispatch, currentFilters]);

  const handleFiltersChange = (newFilters: RenewalDashboardFilterOptions) => {
    dispatch(setCurrentFilters(newFilters));
    
    // Update metrics and charts with new filters
    dispatch(fetchRenewalMetricsRequested(newFilters));
    dispatch(fetchRenewalChartsRequested({ chartType: 'sms-success-rate', filters: newFilters }));
    dispatch(fetchRenewalChartsRequested({ chartType: 'expiry-distribution', filters: newFilters }));
    dispatch(fetchRenewalChartsRequested({ chartType: 'month-wise-trend', filters: newFilters }));
    
    setSnackbar({
      open: true,
      message: 'Filters applied successfully',
      severity: 'success'
    });
  };

  const handleClearFilters = () => {
    dispatch(clearCurrentFilters());
    dispatch(fetchRenewalMetricsRequested({}));
    dispatch(fetchRenewalChartsRequested({ chartType: 'sms-success-rate', filters: {} }));
    dispatch(fetchRenewalChartsRequested({ chartType: 'expiry-distribution', filters: {} }));
    dispatch(fetchRenewalChartsRequested({ chartType: 'month-wise-trend', filters: {} }));
    
    setSnackbar({
      open: true,
      message: 'Filters cleared',
      severity: 'info'
    });
  };

  const handleRefresh = () => {
    dispatch(refreshRenewalDashboardRequested());
    setSnackbar({
      open: true,
      message: 'Dashboard refreshed successfully',
      severity: 'success'
    });
  };

  const handleExport = () => {
    // TODO: Implement export functionality
    setSnackbar({
      open: true,
      message: 'Export functionality coming soon',
      severity: 'info'
    });
  };

  const handlePageChange = (page: number) => {
    dispatch(fetchRenewalPoliciesRequested({
      filters: currentFilters,
      pagination: {
        ...policiesPagination,
        page
      }
    }));
  };

  const handlePageSizeChange = (pageSize: number) => {
    dispatch(fetchRenewalPoliciesRequested({
      filters: currentFilters,
      pagination: {
        ...policiesPagination,
        page: 1,
        page_size: pageSize
      }
    }));
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Action buttons for PageHeader
  const actionButtons = isMobile ? (
    <ButtonGroup variant="outlined" size="small">
      <Tooltip title="Refresh">
        <IconButton
          onClick={handleRefresh}
          disabled={metricsLoading}
          color="primary"
        >
          <RefreshIcon />
        </IconButton>
      </Tooltip>
    </ButtonGroup>
  ) : (
    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
      <Button
        variant="contained"
        startIcon={<RefreshIcon />}
        onClick={handleRefresh}
        disabled={metricsLoading}
        sx={{
          borderRadius: 2,
          textTransform: 'none',
          fontWeight: 500,
          boxShadow: 2,
          '&:hover': {
            boxShadow: 4
          }
        }}
      >
        Refresh
      </Button>
    </Box>
  );

  return (
    <PageLayout>
      <PageHeader
        icon={<DashboardIcon />}
        title="Renewal Notice Dashboard"
        action={actionButtons}
      />

      {/* Error Display */}
      {(metricsError || filtersError || chartsError || policiesError) && (
        <Alert 
          severity="error" 
          sx={{ 
            marginBottom: 2,
            borderRadius: 2,
            boxShadow: 2
          }}
        >
          {metricsError || filtersError || chartsError || policiesError}
        </Alert>
      )}

      {/* Filters Panel - Always visible */}
      <Box sx={{ marginBottom: 2 }}>
        <RenewalFiltersPanel
          filters={filters}
          loading={filtersLoading}
          error={filtersError}
          onFiltersChange={handleFiltersChange}
          onClearFilters={handleClearFilters}
        />
      </Box>

      {/* Metrics Cards */}
      <Box sx={{ marginBottom: 2 }}>
        <RenewalMetricsCards
          metrics={metrics}
          loading={metricsLoading}
          error={metricsError}
          filters={currentFilters}
        />
      </Box>

      {/* Charts Section */}
      <Box sx={{ marginBottom: 2 }}>
        <RenewalChartsSection
          smsSuccessChart={smsSuccessChart}
          expiryDistributionChart={expiryDistributionChart}
          monthTrendChart={monthTrendChart}
          loading={chartsLoading}
          error={chartsError}
        />
      </Box>

      {/* Policies Table */}
      {isPoliciesOpen && (
        <Box sx={{ marginBottom: 2 }}>
          <RenewalPoliciesTable
            policies={policies}
            loading={policiesLoading}
            error={policiesError}
            pagination={policiesPagination}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            onRefresh={handleRefresh}
            onExport={handleExport}
          />
        </Box>
      )}

      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="refresh"
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          zIndex: 1000
        }}
        onClick={handleRefresh}
        disabled={metricsLoading}
      >
        <RefreshIcon />
      </Fab>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageLayout>
  );
};

export default RenewalDashboard;
